<script setup lang="ts">
import { fetchPage } from '@corporate/api'
import Page from '@corporate/components/Page/Page.vue'
import { useAsyncPagePopup } from '@corporate/composables'
import { useDomain } from '@corporate/composables'
import type { ImageApiModel, CorporateLinkApiModel } from '@gro-up/api/corporate'
import { Container, Grid, Typography, Image, SpeechBalloon, SvgComponent } from '@gro-up/ui'
import { AsyncPagePopup } from '@gro-up/ui/components'
import { Pagination, Parallax, Keyboard, A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed } from 'vue'

import TabsComponent from './TabsComponent.vue'

import 'swiper/css'
import 'swiper/css/pagination'

type LocationHeroProps = {
  tabs: CorporateLinkApiModel[]
  title: string
  subTitle: {
    tag?: string
    address: string
  }
  usps?: string[]
  media: ImageApiModel[]
  mediaOverviewUrl: CorporateLinkApiModel
  quote?: string
}

const SWIPER_MODULES = [Pagination, Parallax, Keyboard, A11y]

// Slides are 200px high and as wide as the viewport allows (1.05 per view, 2.2 from 700px),
// so request them by width. The swiper is hidden from lg, so keep that download small.
const SWIPER_IMAGE_SIZES = 'xs:90vw sm:90vw md:42vw lg:300px'

const props = defineProps<LocationHeroProps>()

const { tabs, title, subTitle, usps, media, mediaOverviewUrl, quote } = props

const domain = useDomain()

const decorativeIcon = computed(() => {
  switch (domain.value) {
    case 'child-care':
      return 'kinderopvang-decorative'

    case 'maternity-care':
      return 'maternity-care-decorative'

    case 'youth-support':
      return 'youth-support-decorative'

    case 'community-work':
      return 'icon-buurtwerk'

    default:
      return 'kinderopvang-decorative'
  }
})

const mediaGalleryPage = { url: mediaOverviewUrl.url }

const { isPagePopupOpen, openedPageIndex, onPageLinkClick } = useAsyncPagePopup([mediaGalleryPage])
</script>

<template>
  <div data-component="LocationHero">
    <header class="h-full overflow-hidden">
      <Container as="div" class="h-full">
        <Grid class="h-full items-center">
          <div class="flex flex-col col-start-1 col-end-13 lg:col-end-7">
            <TabsComponent v-if="tabs && tabs.length > 0" :tabs="tabs" class="inline-flex align-self-center w-fit mx-auto lg:mx-0 mb-3 lg:mb-7" />

            <Typography as="h1" variant="header-2" class="text-center mb-2 lg:text-left">{{ title }}</Typography>

            <div class="flex flex-col items-center justify-center lg:flex-row lg:justify-start gap-2 lg:mb-14">
              <div v-if="subTitle.tag" class="inline-flex items-center gap-1">
                <img src="/thumb.png" alt="" >

                <Typography as="span" variant="main-text-condensed-lh" class="text-primary-60 theme:text-primary-60">
                  {{ subTitle.tag }}
                </Typography>
              </div>

              <span v-if="subTitle.tag" class="tag hidden lg:block bg-primary-60 w-1 h-1 rounded-full" />

              <Typography as="span" variant="main-text-condensed-lh" class="text-primary-60 theme:text-primary-60">
                {{ subTitle.address }}
              </Typography>
            </div>

            <!-- swiper for mobile -->

            <Swiper
              :breakpoints="{
                0: {
                  slidesPerView: 1.05,
                  centeredSlides: false,
                },
                700: {
                  slidesPerView: 2.2,
                  centeredSlides: false,
                },
              }"
              :a11y="{
                enabled: true
              }"
              :space-between="16"
              :keyboard="{
                enabled: true,
                onlyInViewport: true,
              }"
              :modules="SWIPER_MODULES"
              :pagination="{ clickable: true }"
              :parallax="true"
              class="location-swiper mb-6 h-50 overflow-visible! lg:hidden! mt-6">
              <SwiperSlide v-if="media[0]">
                <SvgComponent :icon="decorativeIcon" class="absolute -top-4 right-0 w-[62px] h-[62px]" data-swiper-parallax="50" />

                <div class="rounded-2xl overflow-hidden h-full w-full relative">
                  <Image :image="media[0]" :sizes="SWIPER_IMAGE_SIZES" />
                  <button
                    class="cursor-pointer absolute inset-0 w-full h-full"
                    @click="onPageLinkClick($event, mediaGalleryPage)">
                    <span class="flex gap-2 items-center absolute bottom-2 right-3 text-white font-bold text-[12px]">
                      {{ mediaOverviewUrl.title }}

                      <SvgComponent icon="icon-gallery-grid" />
                    </span>
                  </button>
                </div>
              </SwiperSlide>

              <SwiperSlide v-if="media[1]">
                <div class="rounded-2xl overflow-hidden h-full w-full">
                  <Image :image="media[1]" :sizes="SWIPER_IMAGE_SIZES" />
                </div>
              </SwiperSlide>

              <SwiperSlide v-if="quote">
                <SpeechBalloon arrow-position="bottom-center-left" variant="domain">
                  {{ quote }}
                </SpeechBalloon>
              </SwiperSlide>

              <SwiperSlide v-else-if="media[2]">
                <div class="rounded-2xl overflow-hidden h-full w-full">
                  <Image :image="media[2]" :sizes="SWIPER_IMAGE_SIZES" />
                </div>
              </SwiperSlide>
            </Swiper>

            <!-- /swiper for mobile -->
            <ul
              v-if="usps && usps.length > 0"
              class="flex flex-col gap-4 mt-4 lg:mt-0 lg:gap-0">
              <li
                v-for="(usp, index) in usps"
                :key="`${usp}-${index}`"
                class="flex items-baseline gap-2 text-[12px] lg:text-[16px]">
                <Typography as="div" class="flex flex-row items-baseline" variant="main-article">
                  <SvgComponent icon="icon-checkmark" class="w-4 h-4 mr-4" />
                  <span>{{ usp }}</span>
                </Typography>
              </li>
            </ul>

          </div>

          <div class="col-start-1 col-end-13 lg:col-start-7 lg:col-end-13 relative hidden lg:block  h-fit">
            <div class="grid grid-cols-6 grid-rows-2 gap-x-10 gap-y-10 h-fit">
              <div class="row-start-1 col-start-1 col-end-2 relative">
                <SvgComponent :icon="decorativeIcon" class="absolute bottom-0 left-5 z-0 w-[62px] h-[62px]" />
              </div>

              <button v-if="media[0]" class="col-start-2 col-end-7 row-start-1 relative  h-fit" @click="onPageLinkClick($event, mediaGalleryPage)">
                <div class="w-full rounded-2xl overflow-hidden max-h-[304px]">
                  <!-- Width-driven: the column is fluid (max ~577px), the height is capped by max-h -->
                  <Image :image="media[0]" :lazy="false" sizes="580px" />
                  <div
                    class="after:absolute after:inset-0 after:z-10 cursor-pointer"
                  >
                    <span class="flex gap-2 items-center absolute bottom-4 right-4 text-white font-bold text-[14px]">
                      {{ mediaOverviewUrl.title }}

                      <SvgComponent icon="icon-gallery-grid" />
                    </span>
                  </div>
                </div>
              </button>

              <div v-if="media[1]" class="col-start-1 col-end-4 row-start-2 relative h-fit">
                <SvgComponent icon="icon-voetbal" class="absolute bottom-0 right-full -translate-x-12 z-0" />

                <div class="rounded-2xl overflow-hidden relative z-10 h-75 aspect-square">
                  <Image :image="media[1]" :lazy="false" :width="300" :height="300" />
                </div>
              </div>

              <div v-if="quote" class="w-full col-start-4 col-end-7 row-start-2 relative h-fit">
                <SpeechBalloon
                  arrow-position="bottom-center-left"
                  size="md"
                  variant="domain"
                  class="relative z-10">
                  {{ quote }}
                </SpeechBalloon>

                <SvgComponent :icon="decorativeIcon" class="absolute -bottom-24 right-12 z-0 w-[47px] h-[47px] h-fit" />
              </div>

              <div v-else-if="media[2]" class="w-full col-start-4 col-end-7 row-start-2 relative h-fit">
                <div class="rounded-2xl overflow-hidden relative z-10 aspect-[296/200]">
                  <Image :image="media[2]" :lazy="false" :width="296" :height="200" />
                </div>

                <SvgComponent :icon="decorativeIcon" class="absolute -bottom-24 right-12 z-0 w-[47px] h-[47px] h-fit" />
              </div>
            </div>
          </div>
        </Grid>
      </Container>
    </header>

    <!-- AsyncPagePopup for media gallery overlay -->
    <AsyncPagePopup
      v-model="isPagePopupOpen"
      :urls="[mediaGalleryPage.url]"
      :initial-url-index="openedPageIndex"
      :domain="domain"
      :fetch-page="fetchPage"
      background="neutral">
      <template #default="{ page }">
        <Page :page="page" />
      </template>
    </AsyncPagePopup>
  </div>
</template>

<style lang="scss" scoped>
.location-swiper {
  width: 100% !important;

  height: 200px !important;

  :deep(.swiper-pagination) {
    bottom: -30px !important;
  }

  :deep(.swiper-pagination-bullet) {
    background-color: var(--color-head-office-60);

    transition: all 0.3s ease;
  }

  :deep(.swiper-pagination-bullet-active) {
    background-color: var(--color-primary-100);
  }
}
</style>

